import * as React from "react"
import { Button, Dialog, DialogContent, DialogTitle, Icon, cn } from "@nexus/ui"
import { useTranslation } from "react-i18next"
import { useResolveReferenceLinks, toResolvedLinkResults, type ResolvedLinkResult } from "@/lib/queries"

const MAX_DESCRIPTION = 10000
const MAX_LINKS = 3
const VARIATION_OPTIONS = [1, 2, 3, 4, 5] as const

/**
 * `edit` — the form; `resolving` — awaiting the resolve-links pre-step
 * (button shows "Fetching links…", inputs locked); `review` — one or more
 * links failed to resolve, so generation is gated until the user picks
 * "Generate without them" or "Cancel" back to editing.
 */
type ModalPhase = "edit" | "resolving" | "review"

/** A reference-link input row. The stable `id` is used as the React key so
 *  removing a row doesn't shift keys and swap values/focus between inputs. */
type LinkField = { id: string; value: string }

export interface SuggestScriptModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (description: string, variations: number, referenceUrls: string[]) => void
  isSubmitting?: boolean
  error?: string | null
  /** Needed to run the resolve-links pre-step when reference links are present. */
  setId: string
  tenantId: string
}

function isValidHttpUrl(value: string): boolean {
  try {
    const u = new URL(value.trim())
    return u.protocol === "http:" || u.protocol === "https:"
  } catch {
    return false
  }
}

export function SuggestScriptModal({
  open,
  onClose,
  onSubmit,
  isSubmitting = false,
  error,
  setId,
  tenantId,
}: SuggestScriptModalProps) {
  const { t } = useTranslation()
  const [description, setDescription] = React.useState("")
  const [variations, setVariations] = React.useState(3)
  const [links, setLinks] = React.useState<LinkField[]>([])
  const [phase, setPhase] = React.useState<ModalPhase>("edit")
  const [reviewResults, setReviewResults] = React.useState<ResolvedLinkResult[]>([])
  const textareaRef = React.useRef<HTMLTextAreaElement>(null)
  const resolveLinks = useResolveReferenceLinks()

  React.useEffect(() => {
    if (open) {
      setDescription("")
      setVariations(3)
      setLinks([])
      setPhase("edit")
      setReviewResults([])
      requestAnimationFrame(() => textareaRef.current?.focus())
    }
  }, [open])

  const cleanedLinks = React.useMemo(
    () => Array.from(new Set(links.map((l) => l.value.trim()).filter(Boolean))).slice(0, MAX_LINKS),
    [links],
  )
  const invalidLink = cleanedLinks.some((l) => !isValidHttpUrl(l))

  const isResolving = phase === "resolving"
  const isLocked = isSubmitting || phase !== "edit"
  const canSubmit = description.trim().length > 0 && !invalidLink && !isLocked

  const failedResults = React.useMemo(
    () => reviewResults.filter((r) => !r.ok),
    [reviewResults],
  )

  async function handleSubmit() {
    const trimmed = description.trim()
    if (!trimmed || invalidLink || isLocked) return

    const validLinks = cleanedLinks.filter(isValidHttpUrl)
    if (validLinks.length === 0) {
      onSubmit(trimmed, variations, [])
      return
    }

    setPhase("resolving")
    try {
      const result = await resolveLinks.mutateAsync({ setId, referenceUrls: validLinks, tenantId })
      const results = toResolvedLinkResults(result)
      // Represent every submitted link explicitly: a link with no matching result
      // (empty/short/malformed response) counts as unreadable rather than being
      // silently dropped — the whole point of this step is to tell the user.
      const byUrl = new Map(results.map((r) => [r.url, r]))
      const perLink: ResolvedLinkResult[] = validLinks.map(
        (url) =>
          byUrl.get(url) ?? {
            url,
            ok: false,
            reason: t("opportunities.scripts.suggest.linkUnchecked", {
              defaultValue: "couldn't be checked",
            }),
          },
      )
      const okUrls = perLink.filter((r) => r.ok).map((r) => r.url)
      if (okUrls.length === validLinks.length) {
        onSubmit(trimmed, variations, okUrls)
        return
      }
      setReviewResults(perLink)
      setPhase("review")
    } catch {
      // useResolveReferenceLinks already surfaces a toast on failure — just
      // unlock the form so the user can retry or edit the links.
      setPhase("edit")
    }
  }

  function handleReviewCancel() {
    setPhase("edit")
  }

  function handleGenerateWithoutFailed() {
    const okUrls = reviewResults.filter((r) => r.ok).map((r) => r.url)
    onSubmit(description.trim(), variations, okUrls)
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="sm:max-w-2xl gap-0 p-0">
        <div className="flex items-center gap-3 border-b border-border/60 px-6 py-5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[var(--ai-fill-chip)]">
            <Icon name="sparkles" size="sm" className="text-[var(--ai-text)]" />
          </div>
          <DialogTitle className="text-base font-semibold leading-snug">
            {t("opportunities.scripts.suggest.title")}
          </DialogTitle>
        </div>

        {phase === "review" ? (
          <div className="flex flex-col gap-4 px-6 py-5">
            <div className="flex items-start gap-2">
              <Icon name="alert-circle" size="sm" className="mt-0.5 shrink-0 text-destructive" />
              <div className="flex flex-col gap-1">
                <p className="text-sm font-medium text-foreground">
                  {t("opportunities.scripts.suggest.reviewTitle", { count: failedResults.length })}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("opportunities.scripts.suggest.reviewSubtitle")}
                </p>
              </div>
            </div>
            <ul className="flex flex-col gap-2">
              {failedResults.map((r) => (
                <li
                  key={r.url}
                  className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive"
                >
                  {t("opportunities.scripts.suggest.linkFailed", { url: r.url, reason: r.reason })}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="flex flex-col gap-4 px-6 py-5">
            <p className="text-sm text-muted-foreground">
              {t("opportunities.scripts.suggest.description")}
            </p>

            <textarea
              ref={textareaRef}
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, MAX_DESCRIPTION))}
              placeholder={t("opportunities.scripts.suggest.placeholder")}
              rows={4}
              disabled={isLocked}
              className={cn(
                "w-full resize-none rounded-lg border bg-transparent px-3 py-2.5 text-sm outline-none transition-[color,box-shadow]",
                "placeholder:text-muted-foreground/50",
                "focus:border-[var(--ai)] focus:ring-[3px] focus:ring-[var(--ai-fill-chip)]",
                "disabled:cursor-not-allowed disabled:opacity-50",
                error ? "border-destructive" : "border-input",
              )}
            />

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-foreground">
                {t("opportunities.scripts.suggest.referenceLinksLabel")}
              </label>
              <p className="text-xs text-muted-foreground">
                {t("opportunities.scripts.suggest.referenceLinksHelp")}
              </p>
              {links.map((link) => (
                <div key={link.id} className="flex items-center gap-2">
                  <input
                    type="url"
                    value={link.value}
                    onChange={(e) =>
                      setLinks((prev) =>
                        prev.map((l) => (l.id === link.id ? { ...l, value: e.target.value } : l)),
                      )
                    }
                    placeholder={t("opportunities.scripts.suggest.referenceUrlPlaceholder", {
                      defaultValue: "https://…",
                    })}
                    disabled={isLocked}
                    className={cn(
                      "flex-1 rounded-md border bg-transparent px-3 py-2 text-sm outline-none",
                      link.value.trim() && !isValidHttpUrl(link.value)
                        ? "border-destructive"
                        : "border-input",
                    )}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    iconStart="x"
                    aria-label={t("opportunities.scripts.suggest.removeLink")}
                    onClick={() => setLinks((prev) => prev.filter((l) => l.id !== link.id))}
                    disabled={isLocked}
                  />
                </div>
              ))}
              {links.length < MAX_LINKS && (
                <Button
                  variant="outline"
                  size="sm"
                  iconStart="plus"
                  onClick={() =>
                    setLinks((prev) => [...prev, { id: crypto.randomUUID(), value: "" }])
                  }
                  disabled={isLocked}
                  className="self-start"
                >
                  {t("opportunities.scripts.suggest.addLink")}
                </Button>
              )}
            </div>

            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {t("opportunities.scripts.suggest.variationsLabel")}
              </span>
              <div className="flex items-center gap-1.5">
                {VARIATION_OPTIONS.map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setVariations(n)}
                    disabled={isLocked}
                    aria-pressed={variations === n}
                    className={cn(
                      "h-8 w-8 rounded-md border text-sm tabular-nums transition-colors disabled:opacity-50",
                      variations === n
                        ? "border-primary/40 bg-primary/5 text-foreground"
                        : "border-border text-muted-foreground hover:bg-muted hover:text-foreground",
                    )}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <p className="flex items-center gap-1.5 text-xs text-destructive">
                <Icon name="alert-circle" size="xs" />
                {error}
              </p>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-border/60 px-6 py-4">
          {phase === "review" ? (
            <>
              <Button variant="outline" size="sm" onClick={handleReviewCancel}>
                {t("common.cancel")}
              </Button>
              <Button size="sm" iconStart="sparkles" onClick={handleGenerateWithoutFailed}>
                {t("opportunities.scripts.suggest.generateWithout")}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={onClose}>
                {t("common.cancel")}
              </Button>
              <Button
                size="sm"
                iconStart={isResolving || isSubmitting ? "loader" : "sparkles"}
                onClick={handleSubmit}
                disabled={!canSubmit}
                className={isResolving || isSubmitting ? "[&_svg]:animate-spin" : ""}
              >
                {isResolving
                  ? t("opportunities.scripts.suggest.fetchingLinks")
                  : t("opportunities.scripts.suggest.submit", { count: variations })}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
